<div class="brd">
  <div class="brd__header">
    <div class="brd__title-row">
      <div class="brd__title-block">
        <h2 class="brd__title">Reassign meeting</h2>
        @if (booking?.name) {
          <div class="brd__event-line">
            <span class="brd__event-dot" [style.background]="booking?.event?.color?.primary || '#8247F5'"></span>
            <span class="brd__event-name">{{ booking?.name }}</span>
          </div>
        }
      </div>
      <button type="button" class="brd__close" (click)="onCancel()" aria-label="Close">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"></line>
          <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"></line>
        </svg>
      </button>
    </div>
  </div>

  <div class="brd__body">

    <!-- Current meeting summary — read-only. Kept minimal for v1;
         extra fields (location, timezone, custom answers, etc.) can drop
         in here as later chunks land. -->
    <section class="brd__section">
      <div class="brd__section-label">Meeting details</div>
      <div class="brd__detail-grid">
        <div class="brd__detail-row">
          <span class="brd__detail-key">When</span>
          <span class="brd__detail-val">{{ booking?.start | date: 'EEEE, MMMM d, y' }} · {{ booking?.start | date: 'shortTime' }} – {{ booking?.end | date: 'shortTime' }}</span>
        </div>
        <div class="brd__detail-row">
          <span class="brd__detail-key">Current host</span>
          <span class="brd__detail-val">{{ currentHostDisplayName }}</span>
        </div>
        @if (primaryInviteeDisplayName) {
          <div class="brd__detail-row">
            <span class="brd__detail-key">Invitee</span>
            <span class="brd__detail-val">
              {{ primaryInviteeDisplayName }}
              @if (primaryInviteeEmail && primaryInviteeEmail !== primaryInviteeDisplayName) {
                <span class="brd__detail-sub">{{ primaryInviteeEmail }}</span>
              }
            </span>
          </div>
        }
      </div>
    </section>

    <!-- Recruiter picker. `passedUsers` is pre-loaded from the availability
         service so the list only surfaces admins with a schedule set up -
         a recruiter without one would render an empty calendar anyway. -->
    <section class="brd__section">
      <div class="brd__section-label">New host</div>
      @if (!hostsLoaded) {
        <div class="brd__inline-hint">Loading recruiters…</div>
      } @else if (!availableHosts?.length) {
        <div class="brd__inline-warn">
          No other recruiters have availability set up. Set one up via the Availability admin tab first.
        </div>
      } @else {
        <user-filter-select
          [currentValue]="selectedHost"
          (currentValueChange)="onHostChange($event)"
          [multiple]="false"
          [useFullUser]="true"
          [includeAllOption]="false"
          [allowUserSearch]="true"
          [passedUsers]="availableHosts"
          [showUserType]="true"
          placeholder="Select recruiter">
        </user-filter-select>
      }
      @if (isSameAsCurrentHost) {
        <div class="brd__inline-warn">
          You've picked the same recruiter this meeting is already assigned to.
        </div>
      }
      @if (previewLoading && selectedHost?._id && !isSameAsCurrentHost) {
        <div class="brd__inline-hint">Checking template match and availability…</div>
      }
      @if (!previewLoading && previewResult && previewStatusText) {
        <div class="brd__preview-status" [class.brd__preview-status--green]="previewStatusClass === 'green'"
                                          [class.brd__preview-status--amber]="previewStatusClass === 'amber'"
                                          [class.brd__preview-status--red]="previewStatusClass === 'red'">
          {{ previewStatusText }}
        </div>
      }
    </section>

    <!-- Meeting time. Radio between keep-current and pick-new. Calendar
         only renders once we have both a picked host AND their booking
         username - otherwise the slot fetch has nothing to scope by. -->
    <section class="brd__section">
      <div class="brd__section-label">Meeting time</div>
      <div class="brd__radio-group">
        <label class="brd__radio" [class.brd__radio--disabled]="keepCurrentDisabled">
          <input type="radio"
                 name="reassign-time-choice"
                 value="keep"
                 [checked]="timeChoice === 'keep'"
                 [disabled]="keepCurrentDisabled"
                 (change)="onTimeChoiceChange('keep')">
          <span class="brd__radio-label">Keep current time</span>
        </label>
        @if (keepCurrentDisabled) {
          <div class="brd__radio-hint">{{ keepCurrentDisabledReason }}</div>
        }
        <label class="brd__radio">
          <input type="radio"
                 name="reassign-time-choice"
                 value="new"
                 [checked]="timeChoice === 'new'"
                 (change)="onTimeChoiceChange('new')">
          <span class="brd__radio-label">Pick a new time</span>
        </label>
      </div>

      @if (timeChoice === 'new') {
        @if (!selectedHost?._id) {
          <div class="brd__inline-hint">Pick a new host above to load their available times.</div>
        } @else if (loadingNewHostSettings) {
          <div class="brd__inline-hint">Loading availability for {{ selectedHost?.first_name || 'recruiter' }}…</div>
        } @else if (showCalendar) {
          <div class="brd__calendar-wrap">
            <booking-calendar
              [eventId]="bookingEventId"
              [event]="booking?.event"
              [hostUsername]="newHostBookingUsername"
              [refreshTrigger]="calendarRefreshTrigger"
              [alwaysShowSlotsColumn]="true"
              [timezoneAboveCalendar]="true"
              [selectedSlotStart]="currentBookingStart"
              title="Select a new date & time"
              (slotSelected)="onSlotSelected($event)"
              (slotDeselected)="onSlotDeselected()">
            </booking-calendar>
          </div>
        } @else {
          <div class="brd__inline-hint">Unable to load availability for the selected recruiter.</div>
        }
      }
    </section>

    <!-- Optional admin note — surfaces in the reassign audit history and
         eventually in the notification emails. Free text, no length cap
         enforced client-side beyond a soft visual cap. -->
    <section class="brd__section">
      <div class="brd__section-label">Note (optional)</div>
      <textarea class="brd__textarea"
                rows="3"
                maxlength="500"
                placeholder="Add context for the recruiters and invitee (e.g., reason for reassignment)"
                [(ngModel)]="note"></textarea>
    </section>

  </div>

  @if (submitError) {
    <div class="brd__submit-error">{{ submitError }}</div>
  }

  <div class="brd__actions">
    <button type="button" class="brd__btn brd__btn--secondary" [disabled]="submitting" (click)="onCancel()">Cancel</button>
    <button type="button" class="brd__btn brd__btn--primary" [disabled]="!canConfirm || submitting" (click)="onConfirm()">
      @if (submitting) {
        <mat-spinner diameter="16"></mat-spinner>
        <span>Reassigning…</span>
      } @else {
        <span>Reassign</span>
      }
    </button>
  </div>
</div>
